// ABOUTME: Firebase authentication service that handles user login, logout, and token management
// ABOUTME: Provides Firebase ID tokens for authenticated API calls to the backend

import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword as firebaseSignIn,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged
} from 'firebase/auth';
import firebaseConfig from '../Config/firebase.config.js';

/**
 * FirebaseService
 *
 * PURPOSE:
 * This service initializes Firebase and manages all authentication operations.
 * It provides methods for user login/logout and generates Firebase ID tokens
 * that are used to authenticate API requests to our backend.
 *
 * ROLE IN SYSTEM:
 * - Initializes Firebase SDK with project configuration
 * - Handles user authentication (email/password)
 * - Generates Firebase ID tokens for backend API authentication
 * - Manages auth state changes
 * - Used by SessionManager to handle user sessions
 */

class FirebaseService {
  constructor() {
    this.app = null;
    this.auth = null;
    this.initialized = false;
  }

  /**
   * Initialize Firebase with project configuration
   * This should be called once when the application loads
   *
   * @returns {Promise<void>}
   */
  async initializeFirebase() {
    if (this.initialized) {
      console.log('[FirebaseService] Already initialized');
      return;
    }

    try {
      // Check if Firebase is already initialized
      if (getApps().length > 0) {
        this.app = getApp();
        console.log('[FirebaseService] Using existing Firebase app');
      } else {
        // Initialize Firebase with config from environment
        this.app = initializeApp(firebaseConfig);
        console.log('[FirebaseService] Firebase app initialized');
      }

      this.auth = getAuth(this.app);
      this.initialized = true;
      console.log('[FirebaseService] Firebase initialized successfully');
    } catch (error) {
      console.error('[FirebaseService] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Sign in user with email and password
   *
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<Object>} Firebase user object
   */
  async signInWithEmailAndPassword(email, password) {
    if (!this.initialized) {
      throw new Error('FirebaseService not initialized. Call initializeFirebase() first.');
    }

    try {
      const userCredential = await firebaseSignIn(this.auth, email, password);
      console.log('[FirebaseService] User signed in:', userCredential.user.email);
      return userCredential.user;
    } catch (error) {
      console.error('[FirebaseService] Sign in failed:', error);
      throw error;
    }
  }

  /**
   * Sign out current user
   *
   * @returns {Promise<void>}
   */
  async signOut() {
    if (!this.initialized) {
      throw new Error('FirebaseService not initialized. Call initializeFirebase() first.');
    }

    try {
      await firebaseSignOut(this.auth);
      console.log('[FirebaseService] User signed out');
    } catch (error) {
      console.error('[FirebaseService] Sign out failed:', error);
      throw error;
    }
  }

  /**
   * Get currently authenticated user
   *
   * @returns {Object|null} Firebase user object or null if not authenticated
   */
  getCurrentUser() {
    if (!this.initialized) {
      console.warn('[FirebaseService] Not initialized, returning null');
      return null;
    }

    return this.auth.currentUser;
  }

  /**
   * Get Firebase ID token for current user
   * This token is used in Authorization header for backend API calls
   *
   * @returns {Promise<string|null>} Firebase ID token or null if not authenticated
   */
  async getIdToken() {
    if (!this.initialized) {
      console.warn('[FirebaseService] Not initialized, returning null');
      return null;
    }

    const user = this.getCurrentUser();
    if (!user) {
      return null;
    }

    try {
      const token = await user.getIdToken();
      return token;
    } catch (error) {
      console.error('[FirebaseService] Failed to get ID token:', error);
      throw error;
    }
  }

  /**
   * Listen for authentication state changes
   *
   * @param {Function} callback - Called when auth state changes with user object
   * @returns {Function} Unsubscribe function
   */
  onAuthStateChanged(callback) {
    if (!this.initialized) {
      throw new Error('FirebaseService not initialized. Call initializeFirebase() first.');
    }

    return firebaseOnAuthStateChanged(this.auth, callback);
  }
}

// Create singleton instance
const firebaseService = new FirebaseService();

// ES module export
export default firebaseService;